import { useCallback, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import Auth0ProviderWithNavigate from './components/auth/Auth0Provider';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
// Side-effect import: configures the i18next singleton before any component
// calls useTranslation. Must run before LocaleProvider mounts.
import i18n from './i18n';
import { LocaleProvider } from './i18n/LocaleProvider';
import { JobNotificationEvent, useGlobalJobNotifications } from './hooks/useJobNotifications';
import { jobKeys } from './hooks/useJobOffers';
import { getUsageLimitExceededHandler } from './hooks/usePlanUsageGuard';
import './index.css';
import { Toaster, toast } from 'sonner';
import { CheckCircle2, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { LandingPage } from './pages/LandingPage';
// Talent landing (/talent) is disabled; kept in the codebase for a possible revival.
// import { TalentLandingPage } from './pages/TalentLandingPage';
import { SuspendedPage } from './pages/SuspendedPage';
import { Dashboard } from './pages/Dashboard';
import { CallbackPage } from './pages/CallbackPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { InvitationLandingPage } from './pages/InvitationLandingPage';
import { AdminUserCleanupPage } from './pages/AdminUserCleanupPage';
import { JobDetailsPage } from './pages/JobDetailsPage';
import TalentPoolPage from './pages/TalentPoolPage';
import { TalentDetailPage } from './pages/TalentDetailPage';
import { ExamConfigPage } from './pages/ExamConfigPage';
import { PublicJobCvUploadPage } from './pages/PublicJobCvUploadPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { InterviewPreparationPage } from './pages/InterviewPreparationPage';
import { ActualInterviewPage } from './pages/ActualInterviewPage';
import { AdminPage } from './pages/AdminPage';
import InvitationAcceptPage from './pages/InvitationAcceptPage';
import InvitationCompletePage from './pages/InvitationCompletePage';
import { ProtectedRouteWrapper, AdminRouteWrapper, InvitedRouteWrapper, AuthenticatedRedirectWrapper } from './components/wrapper/Wrapper';
import NotAuthorizedPage from './pages/NotAuthorized';
import { PaymentFailurePage } from './pages/PaymentResultPages';
// EmailVerificationPage removed — email verification step dropped
import { PackSelectionPage } from './pages/PackSelectionPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { AgentsPage } from './pages/AgentsPage';
import PageMetadataHandler from './components/routing/PageMetadataHandler';
import { GlobalDialogs } from './components/providers/GlobalDialogs';
import { BlogPage } from './pages/BlogPage';
import { BlogPostPage } from './pages/BlogPostPage';
import { SalesDemoPage } from './pages/SalesDemoPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { ProductOnboardingManager } from './components/onboarding';
import { DemoModeProvider } from './components/providers/DemoModeContext';
import { GitexChallengePage } from './pages/GitexChallengePage';
import { ChallengeCvUpload } from './components/gitex-challenge/ChallengeCvUpload';
import { useAttribution } from './hooks/useAttribution';
import { AppShell } from './components/ui/organisms/AppShell';
import { SettingsPage } from './pages/settings/SettingsPage';
import { BillingPage } from './pages/settings/BillingPage';
import { HomePage } from './pages/HomePage';
import { ChatbotWidget } from '@/components/chatbot';

// Attribution capture — first-touch UTM / click-id persistence
const AttributionCapture = () => { useAttribution(); return null; };

// Create a client — auth failures (401/403) skip retries to avoid spinner loops
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        const status =
          (error as { status?: number; response?: { status?: number } } | null)?.status ??
          (error as { response?: { status?: number } } | null)?.response?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 3;
      },
    },
  },
});

// Helper functions for CV processing notifications
const handleCvSuccess = (event: JobNotificationEvent) => {
  if (!event.job_id) return;
  
  const candidateScore = event.details?.candidate_score as number | undefined;
  const fileName = event.details?.file_name as string | undefined;
  
  // Show individual success toast for each CV
  if (candidateScore !== undefined && candidateScore !== null) {
    toast.success(
      i18n.t('common:notifications.cv_processed_scored', {
        score: candidateScore.toFixed(1),
      }),
      { duration: 4000 },
    );
  } else if (fileName) {
    toast.success(i18n.t('common:notifications.cv_processed_filename', { fileName }), {
      duration: 4000,
    });
  } else {
    toast.success(i18n.t('common:notifications.cv_processed'), { duration: 4000 });
  }
};

const handleScoreRecomputedSuccess = (event: JobNotificationEvent) => {
  if (!event.job_id) return;
  
  const fullName = event.details?.full_name as string | undefined;
  const matchScore = event.details?.match_score as number | undefined;

  toast.success(
    i18n.t('common:notifications.score_recomputed', {
      name: fullName ?? '',
      score: matchScore?.toFixed(1) ?? '—',
    }),
    { duration: 2000 },
  );
};

// Helper function for resume bank batch complete notification
const handleResumeBankBatchComplete = (event: JobNotificationEvent) => {
  const successfulCount = event.details?.successful_count as number || 0;
  const failedCount = event.details?.failed_count as number || 0;
  const failedFiles = event.details?.failed_files as Array<{file_name: string, error: string}> || [];
  
  console.log('[App] Resume bank batch complete:', { successfulCount, failedCount });
  
  // Dismiss the processing toast
  toast.dismiss('resume-bank-processing');
  
  // Show success toast if any succeeded
  if (successfulCount > 0) {
    const message =
      successfulCount === 1
        ? i18n.t('common:notifications.resume_processed_one')
        : i18n.t('common:notifications.resume_processed_many', { count: successfulCount });
    toast.success(message);
  }

  if (failedCount > 0) {
    const message =
      failedCount === 1
        ? i18n.t('common:notifications.resume_failed_one')
        : i18n.t('common:notifications.resume_failed_many', { count: failedCount });

    if (failedFiles.length > 0 && failedFiles[0].error) {
      toast.error(
        i18n.t('common:toasts.resume_failed_with_detail', {
          message,
          detail: failedFiles[0].error,
        }),
        { duration: 6000 },
      );
    } else {
      toast.error(message);
    }
  }
};

// Global notification component - single SSE connection for entire app
const GlobalNotifications = () => {
  const queryClient = useQueryClient();

  const handleNotification = useCallback((event: JobNotificationEvent) => {
    console.log('[App] Received global notification:', event);
    
    // Invalidate job queries for significant updates
    const shouldInvalidateJobs = [
      'job_analysis_error',
      'job_analysis_complete',
      'cv_processing_failed',
      'cv_processing_success',
      'score_recomputed_success',
      'duplicate_candidate_detected',
      'usage_limit_exceeded',
      'plan_expired',
      'social_profile_scoring_started',
      'social_profile_scoring_completed',
      'social_profile_scoring_failed',
    ].includes(event.event);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('invirtus:sse-notification', { detail: event })
      );
    }

    // Admin force-disconnect: broadcast variant gets a 0-5s jitter to spread
    // the re-login burst against Auth0 when many users disconnect at once.
    if (event.event === 'force_disconnect' || event.event === 'force_disconnect_all') {
      const delay = event.event === 'force_disconnect_all' ? Math.random() * 5000 : 0;
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('invirtus:auth-unauthorized', {
            detail: { source: 'sse-force-disconnect' },
          })
        );
      }, delay);
      return;
    }
    
    if (shouldInvalidateJobs) {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
      
      // Invalidate usage queries for usage-related events
      const shouldInvalidateUsage = [
        'usage_limit_exceeded',
        'plan_expired',
        'job_analysis_complete',
        'score_recomputed_success', // job-edit rescores consume CV credits
      ].includes(event.event);
      
      if (shouldInvalidateUsage) {
        queryClient.invalidateQueries({ queryKey: ['usage'] });
      }
    }
    
    // Invalidate resume bank queries when batch completes
    if (event.event === 'resume_bank_batch_complete') {
      queryClient.invalidateQueries({ queryKey: ['resumeBank', 'search'] });
    }
    
    // Handle CV processing success - show individual toast
    if (event.event === 'cv_processing_success') {
      handleCvSuccess(event);
    }

    if (event.event === 'score_recomputed_success') {
      handleScoreRecomputedSuccess(event);
    }
    
    // Handle resume bank batch complete
    if (event.event === 'resume_bank_batch_complete') {
      handleResumeBankBatchComplete(event);
    }
    
    // Handle usage limit exceeded - delegated to GlobalDialogs
    if (event.event === 'usage_limit_exceeded') {
      const handler = getUsageLimitExceededHandler();
      if (handler) {
        handler(event);
      }
    }
    
    // Note: cv_processing_failed toasts are handled by useJobNotifications
  }, [queryClient]);

  useGlobalJobNotifications({
    enabled: true,
    onNotification: handleNotification,
  });
  
  return null; // This component only handles side effects
};

/**
 * Root layout: rendered for every route. Mounts providers that require router
 * context (useNavigate, etc.) and UI singletons scoped to authenticated views.
 */
const RootLayout = () => (
  <Auth0ProviderWithNavigate>
    <DemoModeProvider>
      <GlobalNotifications />
      <ProductOnboardingManager />
      <AttributionCapture />
      <PageMetadataHandler />
      <Toaster
        position="top-right"
        duration={3000}
        expand
        gap={6}
        closeButton
        icons={{
          success: <CheckCircle2 className="h-[18px] w-[18px] text-emerald-600" />,
          error: <AlertCircle className="h-[18px] w-[18px] text-red-500" />,
          warning: <AlertTriangle className="h-[18px] w-[18px] text-amber-500" />,
          info: <Info className="h-[18px] w-[18px] text-blue-600" />,
        }}
      />
      <GlobalDialogs />
      <Outlet />
      <ChatbotWidget />
    </DemoModeProvider>
  </Auth0ProviderWithNavigate>
);
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: '/', element: <AuthenticatedRedirectWrapper><LandingPage /></AuthenticatedRedirectWrapper> },
      { path: '/platform', element: <AuthenticatedRedirectWrapper><LandingPage /></AuthenticatedRedirectWrapper> },
      // /talent landing disabled: old links land on the main landing instead.
      // { path: '/talent', element: <AuthenticatedRedirectWrapper><TalentLandingPage /></AuthenticatedRedirectWrapper> },
      { path: '/talent', element: <Navigate to="/" replace /> },
      { path: '/welcome', element: <Navigate to="/" replace /> },
      { path: '/callback', element: <CallbackPage /> },
      { path: '/auth-callback', element: <AuthCallbackPage /> },
      { path: '/login', element: <AuthenticatedRedirectWrapper><LoginPage /></AuthenticatedRedirectWrapper> },
      { path: '/signup', element: <SignupPage /> },
      { path: '/forgot-password', element: <AuthenticatedRedirectWrapper><ForgotPasswordPage /></AuthenticatedRedirectWrapper> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      // Authenticated routes — share AppShell (sidebar + topbar + mobile bottom-tab)
      {
        element: <AppShell />,
        children: [
          {
            path: '/home',
            element: <ProtectedRouteWrapper><InvitedRouteWrapper><HomePage /></InvitedRouteWrapper></ProtectedRouteWrapper>,
          },
          {
            path: '/jobs',
            element: <ProtectedRouteWrapper><InvitedRouteWrapper><Dashboard /></InvitedRouteWrapper></ProtectedRouteWrapper>,
          },
          { path: '/dashboard', element: <Navigate to="/jobs" replace /> },
          {
            path: '/job/:jobId',
            element: <ProtectedRouteWrapper><JobDetailsPage /></ProtectedRouteWrapper>,
          },
          {
            path: '/jobs/:jobId/exam-config',
            element: <ProtectedRouteWrapper><ExamConfigPage /></ProtectedRouteWrapper>,
          },
          // Talent Pool routes — pool list and talent detail (plan 01-04, T-04-03 auth gate)
          {
            path: '/talent-pool',
            element: <ProtectedRouteWrapper><InvitedRouteWrapper><TalentPoolPage /></InvitedRouteWrapper></ProtectedRouteWrapper>,
          },
          {
            path: '/talent-pool/:id',
            element: <ProtectedRouteWrapper><InvitedRouteWrapper><TalentDetailPage /></InvitedRouteWrapper></ProtectedRouteWrapper>,
          },
          {
            path: '/integrations',
            element: <ProtectedRouteWrapper><InvitedRouteWrapper><IntegrationsPage /></InvitedRouteWrapper></ProtectedRouteWrapper>,
          },
          {
            path: '/agents',
            element: <ProtectedRouteWrapper><InvitedRouteWrapper><AgentsPage /></InvitedRouteWrapper></ProtectedRouteWrapper>,
          },
          {
            path: '/settings',
            element: <ProtectedRouteWrapper><InvitedRouteWrapper><SettingsPage /></InvitedRouteWrapper></ProtectedRouteWrapper>,
          },
          {
            path: '/billing',
            element: <ProtectedRouteWrapper><InvitedRouteWrapper><BillingPage /></InvitedRouteWrapper></ProtectedRouteWrapper>,
          },
          {
            path: '/admin',
            element: <AdminRouteWrapper><AdminPage /></AdminRouteWrapper>,
          },
          {
            path: '/admin/users/cleanup',
            element: <AdminRouteWrapper><AdminUserCleanupPage /></AdminRouteWrapper>,
          },
        ],
      },
      { path: '/invite/:token', element: <InvitationLandingPage /> },
      { path: '/invite-legacy/:token', element: <InvitationAcceptPage /> },
      {
        path: '/invite/:token/complete',
        element: <ProtectedRouteWrapper><InvitationCompletePage /></ProtectedRouteWrapper>,
      },
      { path: '/job/:jobId/cv', element: <PublicJobCvUploadPage /> },
      { path: '/interview/:invitationToken', element: <InterviewPreparationPage /> },
      { path: '/actual-interview/:interviewId', element: <ActualInterviewPage /> },
      { path: '/notauthorized', element: <NotAuthorizedPage /> },
      { path: '/payment/failure', element: <PaymentFailurePage /> },
      {
        path: '/onboarding/pack-selection',
        element: <ProtectedRouteWrapper><PackSelectionPage /></ProtectedRouteWrapper>,
      },
      { path: '/blog', element: <BlogPage /> },
      { path: '/blog/:slug', element: <BlogPostPage /> },
      { path: '/suspended', element: <ProtectedRouteWrapper><SuspendedPage /></ProtectedRouteWrapper> },
      { path: '/sales-demo', element: <SalesDemoPage /> },
      { path: '/gitex-challenge', element: <GitexChallengePage /> },
      { path: '/gitex-challenge/cv/:token', element: <ChallengeCvUpload /> },
      { path: '/terms', element: <TermsPage /> },
      { path: '/privacy', element: <PrivacyPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
function App() {
  // Toast animationend listener — fixed lifetime regardless of hover.
  // Lives here so it mounts exactly once for the entire app lifetime.
  useEffect(() => {
    const onAnimEnd = (e: AnimationEvent) => {
      if (e.animationName !== 'invirtus-toast-progress') return;
      const toastEl = (e.target as HTMLElement | null)?.closest?.('[data-sonner-toast]');
      const closeBtn = toastEl?.querySelector('[data-close-button]') as HTMLElement | null;
      closeBtn?.click();
    };
    document.addEventListener('animationend', onAnimEnd);
    return () => document.removeEventListener('animationend', onAnimEnd);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider>
        <RouterProvider router={router} />
      </LocaleProvider>
    </QueryClientProvider>
  );
}

export default App;
